import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "../lib/api";
import { Button, Card, JurisBadge, cx } from "../components/ui";
import { formatNullableAuDate } from "../domain/time";
import {
  ALERT_CHANGE_TYPE_OPTIONS,
  ALERT_KIND_OPTIONS,
  ALERT_STATUS_FILTER_OPTIONS,
  alertCaptureDraftFromFormState,
  alertCaptureView,
  alertDeferCommand,
  alertKindDisplayName,
  alertLawMatches,
  alertListQueryArgs,
  alertStartActionView,
  alertStatusDisplayName,
  alertStatusFilterButtonClass,
  alertStatusFilterDisplayName,
  alertStartStatusCommand,
  alertStatusToneClass,
  changeTypeDisplayName,
  emptyAlertCaptureFormState,
  nextAlertCaptureFormState,
  selectedAlertLaw,
  shouldShowAlertLawMatches,
  type AlertCaptureFormAction,
  type AlertStatusFilter,
} from "../domain/alerts";
import type { AlertStatus } from "../../convex/model/enums";

type AlertRow = FunctionReturnType<typeof api.alerts.list>[number];

export function AlertQueue() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<AlertStatusFilter>("new");
  const alertsQuery = useQuery(api.alerts.list, alertListQueryArgs(status));
  const alerts = useMemo(() => alertsQuery ?? [], [alertsQuery]);
  const rawLegislation = useQuery(api.legislation.list, { status: "active" });
  const legislation = useMemo(() => rawLegislation ?? [], [rawLegislation]);

  const createAlert = useMutation(api.alerts.create);
  const updateAlertStatus = useMutation(api.alerts.updateStatus);

  const [captureState, setCaptureState] = useState(() => emptyAlertCaptureFormState());

  const lawMatches = useMemo(() => alertLawMatches({ legislation, query: captureState.lawSearch }), [captureState.lawSearch, legislation]);
  const selectedLaw = useMemo(
    () => selectedAlertLaw({ legislation, legislationId: captureState.legislationId }),
    [captureState.legislationId, legislation],
  );
  const captureDraft = useMemo(
    () => alertCaptureDraftFromFormState({ state: captureState, linkedLegislation: selectedLaw }),
    [captureState, selectedLaw],
  );
  const captureView = useMemo(() => alertCaptureView(captureDraft), [captureDraft]);

  const updateCapture = (action: AlertCaptureFormAction) => {
    setCaptureState((current) => nextAlertCaptureFormState(current, action));
  };

  const submitAlert = async () => {
    if (!captureView.prepared.ok) return;
    await createAlert(captureView.prepared.value);
    setCaptureState(emptyAlertCaptureFormState());
  };

  const startAlert = async (alert: AlertRow) => {
    const command = alertStartStatusCommand(alert._id, alert.status);
    if (command === null) return;
    await updateAlertStatus(command);
  };

  return (
    <div className="mx-auto w-full max-w-[1240px] px-6 py-7 lg:px-9">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-slate-900">Legislative alert queue</h1>
          <p className="mt-1 text-sm text-slate-500">Triage source alerts, confirm commencement, and create update-log entries for subscribed legislation.</p>
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg bg-white p-1 ring-1 ring-slate-200">
          {ALERT_STATUS_FILTER_OPTIONS.map((option) => (
            <button
              key={option}
              onClick={() => setStatus(option)}
              className={cx(
                "rounded px-2.5 py-1.5 text-[12.5px] font-medium",
                alertStatusFilterButtonClass({ current: status, option }),
              )}
            >
              {alertStatusFilterDisplayName(option)}
            </button>
          ))}
        </div>
      </div>


      <div className="grid gap-5 xl:grid-cols-[23rem_1fr]">
        <Card className="h-fit p-5">
          <h2 className="mb-3 text-[14px] font-semibold text-slate-900">Capture alert</h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <select aria-label="Alert source type" className="rounded border border-slate-300 px-3 py-2 text-sm" value={captureState.kind} onChange={(e) => updateCapture({ type: "kindInput", value: e.target.value })}>
                {ALERT_KIND_OPTIONS.map((option) => <option key={option} value={option}>{alertKindDisplayName(option)}</option>)}
              </select>
              <select aria-label="Legislative change type" className="rounded border border-slate-300 px-3 py-2 text-sm" value={captureState.changeType} onChange={(e) => updateCapture({ type: "changeTypeInput", value: e.target.value })}>
                {ALERT_CHANGE_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{changeTypeDisplayName(option)}</option>)}
              </select>
            </div>
            <input
              aria-label="Search and link legislation"
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Search/link legislation"
              value={captureState.lawSearch}
              onChange={(e) => updateCapture({ type: "lawSearchInput", value: e.target.value })}
            />
            {shouldShowAlertLawMatches(captureState.lawSearch) && (
              <div className="max-h-40 overflow-y-auto rounded border border-slate-200 bg-white">
                {lawMatches.map((law) => (
                  <button key={law._id} className={cx("flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-slate-50", captureState.legislationId === law._id && "bg-[var(--accent-soft)]")} onClick={() => updateCapture({ type: "selectLegislation", legislation: law })}>
                    <JurisBadge code={law.jurisdiction} />
                    <span className="truncate">{law.title}</span>
                  </button>
                ))}
              </div>
            )}
            <input aria-label="Amending legislation" className="w-full rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Amending legislation" value={captureState.amendingLegislation} onChange={(e) => updateCapture({ type: "amendingLegislationInput", value: e.target.value })} />
            <input aria-label="Commencement date" type="date" className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={captureState.commencementDate} onChange={(e) => updateCapture({ type: "commencementDateInput", value: e.target.value })} />
            <textarea aria-label="Plain-English summary" className="h-20 w-full rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Plain-English summary" value={captureState.summary} onChange={(e) => updateCapture({ type: "summaryInput", value: e.target.value })} />
            <textarea aria-label="Alert notes" className="h-20 w-full rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Notes from alert/commencement check" value={captureState.notes} onChange={(e) => updateCapture({ type: "notesInput", value: e.target.value })} />
            {captureView.problem !== null && <div className="text-[12.5px] text-amber-700">{captureView.problem}</div>}
            <Button className="w-full" icon="plus" disabled={!captureView.canSubmit} onClick={submitAlert}>Add to queue</Button>
          </div>
        </Card>

        <div className="space-y-4">
          {alerts.length === 0 ? (
            <Card className="px-5 py-12 text-center text-sm text-slate-400">No alerts in this view.</Card>
          ) : alerts.map((alert) => {
            const startAction = alertStartActionView(alert.status);
            return (
              <Card key={alert._id} className="overflow-hidden">
                <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
                  <div className="min-w-0">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <StatusBadge status={alert.status} />
                      <span className="rounded bg-slate-50 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500 ring-1 ring-slate-200">{alertKindDisplayName(alert.kind)}</span>
                      {alert.legislation !== null && <JurisBadge code={alert.legislation.jurisdiction} />}
                    </div>
                    <h2 className="truncate text-[15px] font-semibold text-slate-900">{alert.legislation?.title ?? alert.legislationTitle}</h2>
                    <p className="mt-1 text-[13px] leading-relaxed text-slate-600">{alert.summary ?? alert.amendingLegislation}</p>
                  </div>
                  <div className="text-right text-[12.5px] text-slate-400">
                    <div>Received {formatNullableAuDate(alert.receivedAt, "No date")}</div>
                    <div>Commences {formatNullableAuDate(alert.commencementDate, "No date")}</div>
                  </div>
                </div>

                <div className="grid gap-0 lg:grid-cols-[1fr_18rem]">
                  <div className="px-5 py-4">
                    <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-400">Amending instrument</div>
                    <div className="mt-1 text-[13.5px] font-medium text-slate-800">{alert.amendingLegislation}</div>
                    {alert.notes !== null && <p className="mt-3 text-[13px] leading-relaxed text-slate-500">{alert.notes}</p>}
                  </div>

                  <div className="space-y-2 border-t border-slate-100 px-5 py-4 lg:border-l lg:border-t-0">
                    <Button size="sm" className="w-full" icon="pencil" onClick={() => alert.legislationId !== null && navigate("/editor/" + alert.legislationId + "?alert=" + alert._id)} disabled={alert.legislationId === null}>
                      {alert.updateLogId === null ? "Continue in editor" : "Open published change"}
                    </Button>
                    <Button size="sm" variant="outline" className="w-full" icon="clock" onClick={() => startAlert(alert)} disabled={startAction.disabled}>{startAction.buttonLabel}</Button>
                    {alert.updateLogId !== null && <div className="rounded bg-emerald-50 px-2 py-1.5 text-center text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">Content and update record published</div>}
                    <Button size="sm" variant="ghost" className="w-full" onClick={() => updateAlertStatus(alertDeferCommand(alert._id))}>Defer</Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: AlertStatus }) {
  return <span className={cx("rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1", alertStatusToneClass(status))}>{alertStatusDisplayName(status)}</span>;
}
